import styled from '@emotion/styled';
import {
  Button,
  SvgIcon,
  SvgNames,
  Textarea,
  tokens,
} from '@sunrun/experience-ui-components';
import { useEffect, useState } from 'react';
import salesSupportStarLogo from 'assets/images/sales-support-star-logo.svg';
import {
  DASHBOARD_OVERVIEW__CLICKED_ON_ASK_MEGAN_WITH_QUESTION,
  DASHBOARD_OVERVIEW__CLICKED_ON_CURRENT_AI_WITH_QUESTION,
} from 'constants/mixpanelEvents';
import { getUrls } from 'helpers/getUrlHelper';
import { publishMessage } from 'helpers/messaging';
import { Mixpanel } from 'providers/Mixpanel';

const CURRENT_URL = 'https://current.sunrun.com/';

type AskHelpTab = 'megan' | 'current';

type AskHelpWidgetProps = {
  onTabChange?: (tab: AskHelpTab) => void;
};

const TAB_OPTIONS: { label: string; value: AskHelpTab }[] = [
  { label: 'MEGAN', value: 'megan' },
  { label: 'CURRENT', value: 'current' },
];

const AskHelpWidget = ({ onTabChange }: AskHelpWidgetProps) => {
  const [tab, setTab] = useState<AskHelpTab>('megan');
  const [meganQuestion, setMeganQuestion] = useState('');
  const [currentQuestion, setCurrentQuestion] = useState('');

  useEffect(() => {
    onTabChange?.(tab);
  }, [onTabChange, tab]);

  const handleTabChange = (nextTab: AskHelpTab) => {
    setTab(nextTab);
  };

  const handleAskMegan = (question: string) => {
    const trimmed = question.trim();
    if (!trimmed) {
      return;
    }

    const { oneBaseUrl } = getUrls();
    Mixpanel.track(DASHBOARD_OVERVIEW__CLICKED_ON_ASK_MEGAN_WITH_QUESTION, {
      searchTerm: trimmed,
    });
    publishMessage({
      source: 'Dashboard',
      type: 'COMMAND:CHANGE_URL',
      payload: {
        url: `${oneBaseUrl}/megan?prompt=${encodeURIComponent(trimmed)}`,
      },
    });
  };

  const generateCurrentUrl = (term: string) => {
    const encodedTerm = encodeURIComponent('How do I ' + term + ' in ONE?');
    return `${CURRENT_URL}search?term=${encodedTerm}`;
  };

  const handleCurrentQuestion = (question: string = '') => {
    const trimmed = question.trim();
    if (!trimmed) {
      return;
    }

    Mixpanel.track(DASHBOARD_OVERVIEW__CLICKED_ON_CURRENT_AI_WITH_QUESTION, {
      searchTerm: trimmed,
    });
    window.open(generateCurrentUrl(trimmed), '_blank');
  };

  return (
    <Card>
      <Header>
        <TabList
          role="tablist"
          aria-label="Ask help source"
          data-testid="ask-help-tabs">
          {TAB_OPTIONS.map((option) => {
            const isActive = tab === option.value;
            return (
              <TabButton
                key={option.value}
                type="button"
                role="tab"
                aria-selected={isActive}
                $active={isActive}
                onClick={() => handleTabChange(option.value)}>
                <TabLabel $active={isActive}>{option.label}</TabLabel>
                <TabIndicator isActive={isActive} />
              </TabButton>
            );
          })}
        </TabList>
        <ExpandButton
          type="button"
          aria-label={tab === 'megan' ? 'Open Megan' : 'Open Current'}
          onClick={() => openAskHelpDestination(tab)}
          data-testid="ask-help-expand">
          <SvgIcon
            name={SvgNames.ChevronRightCircle}
            width={24}
            height={24}
            color={tokens.BRAND_HERO_BLUE}
          />
        </ExpandButton>
      </Header>

      <Body>
        {tab === 'megan' ? (
          <Panel>
            <MeganIntro>
              <SalesSupportAvatar
                src={salesSupportStarLogo}
                alt="Sales Support"
              />
              <MeganHeadline>
                Get your answers with your AI virtual assistant
              </MeganHeadline>
            </MeganIntro>
            <StyledTextarea
              placeholder="Enter your question about One here"
              value={meganQuestion}
              onChange={(e) => setMeganQuestion(e.target.value)}
              data-testid="ask-megan-input"
            />
            <FullWidthButton
              size="sm"
              color="secondary"
              disabled={!meganQuestion.trim()}
              onClick={() => handleAskMegan(meganQuestion)}
              data-testid="ask-megan-submit">
              Ask Megan
            </FullWidthButton>
          </Panel>
        ) : (
          <Panel>
            <CurrentIntro>
              <CurrentHeadline>
                How do I <BlankUnderline aria-hidden="true" /> in ONE?
              </CurrentHeadline>
              <CurrentSubtitle>
                See ONE user guides and tutorials in Current.
              </CurrentSubtitle>
            </CurrentIntro>
            <StyledTextarea
              placeholder="Enter your question about One here"
              value={currentQuestion}
              onChange={(e) => setCurrentQuestion(e.target.value)}
              data-testid="ask-current-input"
            />
            <FullWidthButton
              size="sm"
              color="secondary"
              disabled={!currentQuestion.trim()}
              onClick={() => handleCurrentQuestion(currentQuestion)}
              data-testid="ask-current-submit">
              See AI answer in Current
              <SvgIcon
                name={SvgNames.ExternalLink}
                height={16}
                width={16}
                color={
                  currentQuestion.trim()
                    ? tokens.BRAND_HERO_BLUE
                    : tokens.TINTS_OFF_WHITE_20
                }
              />
            </FullWidthButton>
          </Panel>
        )}
      </Body>
    </Card>
  );
};

const openAskHelpDestination = (tab: AskHelpTab) => {
  if (tab === 'megan') {
    const { oneBaseUrl } = getUrls();
    publishMessage({
      source: 'Dashboard',
      type: 'COMMAND:CHANGE_URL',
      payload: {
        url: `${oneBaseUrl}/megan`,
      },
    });
    return;
  }

  window.open(CURRENT_URL, '_blank');
};

const Card = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  min-width: 300px;
  box-sizing: border-box;
  border-radius: 8px;
  background-color: rgba(31, 38, 71, 0.04);
  overflow: hidden;

  * {
    box-sizing: border-box;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
  padding: 16px 12px 12px;
  border-bottom: 0.5px solid rgba(255, 255, 255, 0.1);
`;

const TabList = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 8px;
  height: 24px;
`;

const TabButton = styled.button<{ $active: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ $active }) => ($active ? '4px' : '0')};
  min-width: 64px;
  height: ${({ $active }) => ($active ? 'auto' : '32px')};
  padding: ${({ $active }) => ($active ? '8px 4px 0' : '8px 4px')};
  border: none;
  background: none;
  cursor: pointer;
  font-family: inherit;
  height: 32px;

  &:focus-visible {
    outline: 2px solid ${tokens.BRAND_HERO_BLUE};
    outline-offset: 2px;
  }
`;

const TabLabel = styled.span<{ $active: boolean }>`
  font-size: 14px;
  letter-spacing: -0.28px;
  font-weight: ${({ $active }) => ($active ? 500 : 400)};
  color: ${tokens.BRAND_HERO_BLUE};
  white-space: nowrap;
`;

const TabIndicator = styled.div<{ isActive: boolean }>`
  width: 100%;
  height: 3px;
  border-radius: 4px;
  background-color: ${({ isActive }) =>
    isActive ? tokens.BRAND_HERO_BLUE : 'transparent'};
`;

const ExpandButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  flex-shrink: 0;

  &:focus-visible {
    outline: 2px solid ${tokens.BRAND_HERO_BLUE};
    outline-offset: 2px;
  }
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  padding: 0 16px 20px;
  overflow-y: auto;
`;

const Panel = styled.div`
  display: grid;
  grid-template-rows: auto 1fr auto;
  gap: 12px;
  width: 100%;
  height: 100%;
`;

const MeganIntro = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
`;

const SalesSupportAvatar = styled.img`
  width: 47px;
  height: 47px;
  flex-shrink: 0;
  object-fit: contain;
`;

const MeganHeadline = styled.p`
  margin: 0;
  flex: 1;
  min-width: 0;
  font-size: 20px;
  line-height: 1.2;
  letter-spacing: -0.4px;
  font-weight: 500;
  color: ${tokens.BRAND_HERO_BLUE};
`;

const CurrentIntro = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
`;

const CurrentHeadline = styled.p`
  margin: 0;
  font-size: 24px;
  line-height: 1.1;
  letter-spacing: -0.48px;
  font-weight: 500;
  color: ${tokens.BRAND_HERO_BLUE};
`;

const BlankUnderline = styled.span`
  display: inline-block;
  width: 88px;
  border-bottom: 2px solid ${tokens.BRAND_HERO_BLUE};
  vertical-align: baseline;
  margin: 0 4px;
`;

const CurrentSubtitle = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.4;
  letter-spacing: -0.28px;
  font-weight: 400;
  color: ${tokens.BRAND_HERO_BLUE};
`;

const StyledTextarea = styled(Textarea)`
  min-height: 96px;
  width: 100%;

  textarea {
    height: 100%;
    resize: none;
  }
`;

const FullWidthButton = styled(Button)`
  width: 100%;
  justify-content: center;

  > span {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    width: 100%;
    font-size: 16px;
  }
`;

export { AskHelpWidget, openAskHelpDestination };
export type { AskHelpTab };
